/* eslint-disable no-underscore-dangle */
import * as Sentry from '@sentry/browser';
import axios from 'axios';

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

axios.defaults.withCredentials = true;

// Request interceptor with enhanced breadcrumb tracking
axios.interceptors.request.use(request => {
    if (import.meta.env.MODE === 'development') {
        // Create a new Error to get the stack
        const stackError = new Error('Request stack trace');

        // Attach the stack to the request config
        request._requestStack = stackError.stack;
    }

    const adjustedRequest = { ...request };

    adjustedRequest.metadata = { startTime: new Date() };

    const locale = localStorage.getItem('locale');

    if (locale) {
        adjustedRequest.headers.common['Accept-Language'] = locale;
    }

    // CONSULTATION ADDITION: Add breadcrumb for request start
    if (typeof Sentry !== 'undefined' && Sentry.addBreadcrumb) {
        Sentry.addBreadcrumb({
            category: 'api.request',
            message: `${adjustedRequest.method.toUpperCase()} ${adjustedRequest.url}`,
            level: 'info',
            data: {
                method: adjustedRequest.method,
                url: adjustedRequest.url,
                params: adjustedRequest.params,
                timeout: adjustedRequest.timeout,
            },
        });
    }

    return adjustedRequest;
});

// Helper function to safely extract error details
const extractErrorDetails = error => {
    const response = error.response || {};
    const config = error.config || {};

    // Extract meaningful error information
    const errorType = response.data?.error?.type || response.data?.message || 'Unknown error';
    const errorMessage =
        response.data?.error?.message || response.data?.message || response.statusText || 'No error message';
    const errorCode = response.data?.error?.code || response.data?.code || null;

    return {
        type: errorType,
        message: errorMessage,
        code: errorCode,
        status: response.status,
        url: config.url,
        method: config.method?.toUpperCase(),
        data: response.data,
        statusText: response.statusText,
    };
};

// Helper function to create a unique fingerprint for Sentry grouping
const createErrorFingerprint = errorDetails => {
    const fingerprint = ['axios-error'];

    // Add status code to fingerprint
    fingerprint.push(`status-${errorDetails.status}`);

    // Add endpoint pattern to fingerprint (normalize IDs)
    if (errorDetails.url) {
        const normalizedUrl = errorDetails.url
            .replace(/\/\d+/g, '/[id]') // Replace numeric IDs
            .replace(/\/[a-f0-9-]{36}/g, '/[uuid]') // Replace UUIDs
            .replace(/\?.*$/, ''); // Remove query parameters
        fingerprint.push(`endpoint-${normalizedUrl}`);
    }

    // Add method to fingerprint
    if (errorDetails.method) {
        fingerprint.push(`method-${errorDetails.method}`);
    }

    // Add error type/code if available
    if (errorDetails.code) {
        fingerprint.push(`code-${errorDetails.code}`);
    } else if (errorDetails.type && errorDetails.type !== 'Unknown error') {
        fingerprint.push(`type-${errorDetails.type}`);
    }

    return fingerprint;
};

// Helper function to retry requests with exponential backoff
const retryRequest = async (error, retryCount = 0) => {
    const config = error.config || {};
    const maxRetries = config.maxRetries ?? 3;
    const retryDelay = config.retryDelay ?? 1000; // Base delay in ms

    // Get current retry count from config or use the parameter
    const currentRetryCount = config.__retryCount ?? retryCount;

    // Only retry 502 errors and network errors
    const shouldRetry =
        (error.response?.status === 502 || !error.response) && currentRetryCount < maxRetries && config.retry !== false; // Allow opt-out via config.retry = false

    if (!shouldRetry) {
        return Promise.reject(error);
    }

    // Mark this request as being retried
    config.__retryCount = currentRetryCount + 1;

    // Calculate exponential backoff delay: baseDelay * 2^retryCount
    const delay = retryDelay * 2 ** currentRetryCount;

    // Add breadcrumb for retry attempt
    if (typeof Sentry !== 'undefined' && Sentry.addBreadcrumb) {
        Sentry.addBreadcrumb({
            category: 'api.retry',
            message: `Retrying ${config.method?.toUpperCase()} ${config.url} (attempt ${config.__retryCount}/${maxRetries})`,
            level: 'info',
            data: {
                retryCount: config.__retryCount,
                maxRetries,
                delay,
                status: error.response?.status,
            },
        });
    }

    // Wait before retrying
    await new Promise(resolve => setTimeout(resolve, delay));

    // Retry the request
    return axios(config);
};

// eslint-disable-next-line complexity
const errorResponseHandler = async error => {
    // Use let to allow reassignment after retry attempts
    let currentError = error;

    // Handle canceled requests and AbortError early - these are intentional and should not be logged
    // Supports both CancelToken (axios.isCancel) and AbortController (ERR_CANCELED/CanceledError)
    if (
        axios.isCancel?.(currentError) ||
        currentError?.code === 'ERR_CANCELED' ||
        currentError?.name === 'CanceledError' ||
        currentError?.name === 'AbortError' ||
        currentError?.message === 'AbortError' ||
        currentError?.message?.includes('AbortError')
    ) {
        return Promise.reject(currentError);
    }

    // If we had a stack trace from the request config
    if (currentError.config?._requestStack) {
        // Attach it to the error object for later display
        currentError.requestStack = currentError.config._requestStack;
    }

    // Handle network errors (no response) - attempt retry
    if (typeof currentError.response === 'undefined') {
        try {
            return await retryRequest(currentError);
        } catch (retryError) {
            return Promise.reject(retryError);
        }
    }

    let statusCode = currentError.response?.status;
    let errorDetails = extractErrorDetails(currentError);
    const duration = currentError.config?.metadata ? new Date() - currentError.config.metadata.startTime : 0;

    // Attempt retry for 502 errors before processing error
    if (statusCode === 502) {
        try {
            return await retryRequest(currentError);
        } catch (retryError) {
            // If retry failed, continue with error handling below
            // Update currentError and derived values for the final error state
            currentError = retryError;
            statusCode = currentError.response?.status;
            errorDetails = extractErrorDetails(currentError);
        }
    }

    // CONSULTATION ADDITION: Add breadcrumb for all responses (success and error)
    if (typeof Sentry !== 'undefined' && Sentry.addBreadcrumb) {
        Sentry.addBreadcrumb({
            category: 'api.response',
            message: `${errorDetails.method} ${errorDetails.url} [${statusCode}]`,
            level: statusCode >= 500 ? 'error' : 'warning',
            data: {
                status: statusCode,
                statusText: errorDetails.statusText,
                duration,
                url: errorDetails.url,
                method: errorDetails.method,
            },
        });
    }

    // Filter out expected HTTP errors from Sentry reporting
    // These are considered normal application flow, not exceptions
    const expectedErrorCodes = [400, 401, 403, 404, 409, 412, 422, 429];

    // Only report unexpected errors to Sentry (5xx errors and unexpected 4xx errors)
    if (statusCode && !expectedErrorCodes.includes(statusCode)) {
        // Don't report to Sentry if error handling is explicitly disabled for this request
        if (currentError.config?.errorHandle !== false) {
            Sentry.withScope(scope => {
                // Set tags for better filtering in Sentry
                scope.setTag('error_type', 'axios_response_error');
                scope.setTag('http_status', statusCode);
                scope.setTag('http_method', errorDetails.method);
                scope.setTag('error_category', statusCode >= 500 ? 'server_error' : 'client_error');

                // CONSULTATION FIX: Enhanced 502 error tracking
                if (statusCode === 502) {
                    scope.setTag('error.type', '502_gateway');
                    scope.setLevel('error');
                    scope.setContext('gateway_error', {
                        url: errorDetails.url,
                        method: errorDetails.method,
                        params: currentError.config?.params,
                        data: currentError.config?.data,
                        duration,
                        headers: currentError.config?.headers,
                        response_headers: currentError.response?.headers,
                        user_agent: navigator.userAgent,
                        timestamp: new Date().toISOString(),
                        retryCount: currentError.config?.__retryCount || 0,
                        retriesExhausted: currentError.config?.__retryCount >= (currentError.config?.maxRetries ?? 3),
                    });
                }

                // Add endpoint pattern as tag (normalized)
                if (errorDetails.url) {
                    const endpointPattern = errorDetails.url
                        .replace(/\/\d+/g, '/[id]')
                        .replace(/\/[a-f0-9-]{36}/g, '/[uuid]')
                        .replace(/\?.*$/, '');
                    scope.setTag('endpoint_pattern', endpointPattern);
                }

                // Set detailed context
                scope.setContext('request_details', {
                    url: errorDetails.url,
                    method: errorDetails.method,
                    status: statusCode,
                    statusText: errorDetails.statusText,
                    headers: currentError.config?.headers,
                    data: currentError.config?.data,
                    params: currentError.config?.params,
                    timeout: currentError.config?.timeout,
                });

                scope.setContext('response_details', {
                    status: statusCode,
                    statusText: errorDetails.statusText,
                    data: errorDetails.data,
                    headers: currentError.response?.headers,
                    errorType: errorDetails.type,
                    errorMessage: errorDetails.message,
                    errorCode: errorDetails.code,
                });

                // Add timing information if available
                if (currentError.config?.metadata) {
                    scope.setContext('timing', {
                        duration,
                        startTime: currentError.config.metadata.startTime,
                        endTime: new Date(),
                        is_slow: duration > 5000, // Mark slow requests
                    });
                }

                // Create a specific error message that includes meaningful details
                let errorMessage = `HTTP ${statusCode} ${errorDetails.method} ${errorDetails.url}`;

                if (errorDetails.code) {
                    errorMessage += ` - ${errorDetails.code}`;
                }

                if (errorDetails.message && errorDetails.message !== 'Unknown error') {
                    errorMessage += `: ${errorDetails.message}`;
                } else if (errorDetails.statusText) {
                    errorMessage += `: ${errorDetails.statusText}`;
                }

                const sentryError = new Error(errorMessage);
                sentryError.originalError = currentError;

                // Set custom fingerprint for better grouping
                scope.setFingerprint(createErrorFingerprint(errorDetails));

                Sentry.captureException(sentryError);
            });
        }
    }

    return Promise.reject(currentError);
};

axios.interceptors.response.use(response => {
    const adjustment = { ...response };

    adjustment.config.metadata.endTime = new Date();

    adjustment.duration = response.config.metadata.endTime - response.config.metadata.startTime;

    // CONSULTATION ADDITION: Add breadcrumb for successful responses
    if (typeof Sentry !== 'undefined' && Sentry.addBreadcrumb) {
        Sentry.addBreadcrumb({
            category: 'api.response',
            message: `${response.config.method.toUpperCase()} ${response.config.url} [${response.status}]`,
            level: 'info',
            data: {
                status: response.status,
                statusText: response.statusText,
                duration: adjustment.duration,
                url: response.config.url,
                method: response.config.method.toUpperCase(),
            },
        });
    }

    return adjustment;
}, errorResponseHandler);

export { errorResponseHandler };

export default axios;
